import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
const IS_DEV = process.env.NODE_ENV !== 'production';

// Centralized error logger — silent in production so we don't leak internals.
const logError = (msg, err) => {
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    logError(msg, err);
  }
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Properties API
export const propertiesAPI = {
  getAll: async (params = {}) => {
    try {
      const response = await apiClient.get('/properties/', { params });
      return response.data;
    } catch (error) {
      logError('Error fetching properties:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/properties/${id}`);
      return response.data;
    } catch (error) {
      logError(`Error fetching property ${id}:`, error);
      throw error;
    }
  },

  create: async (propertyData) => {
    try {
      const response = await apiClient.post('/properties/', propertyData);
      return response.data;
    } catch (error) {
      logError('Error creating property:', error);
      throw error;
    }
  },

  createWithKYC: async (formData) => {
    try {
      const response = await axios.post(`${API_BASE}/properties/register-with-kyc`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      logError('Error creating property with KYC:', error);
      throw error;
    }
  },

  update: async (propertyId, updates, hostId) => {
    try {
      let url = `/properties/${propertyId}`;
      if (hostId) {
        url += `?host_id=${hostId}`;
      }
      const response = await apiClient.put(url, updates);
      return response.data;
    } catch (error) {
      logError('Error updating property:', error);
      throw error;
    }
  },
};

// Bookings API
export const bookingsAPI = {
  create: async (bookingData) => {
    try {
      const response = await apiClient.post('/bookings/create', bookingData);
      return response.data;
    } catch (error) {
      logError('Error creating booking:', error);
      throw error;
    }
  },

  verifyPayment: async (paymentData) => {
    try {
      const response = await apiClient.post('/bookings/verify-payment', paymentData);
      return response.data;
    } catch (error) {
      logError('Error verifying payment:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/bookings/${id}`);
      return response.data;
    } catch (error) {
      logError(`Error fetching booking ${id}:`, error);
      throw error;
    }
  },

  getByEmail: async (email) => {
    try {
      const response = await apiClient.get(`/bookings/user/${email}`);
      return response.data;
    } catch (error) {
      logError('Error fetching user bookings:', error);
      throw error;
    }
  },

  getByHost: async (hostId) => {
    try {
      const response = await apiClient.get(`/bookings/host/${hostId}`);
      return response.data;
    } catch (error) {
      logError('Error fetching host bookings:', error);
      throw error;
    }
  },

  getHostCancellations: async (hostId) => {
    try {
      const response = await apiClient.get(`/bookings/cancellations/host/${hostId}`);
      return response.data;
    } catch (error) {
      logError('Error fetching host cancellations:', error);
      throw error;
    }
  },

  respondCancellation: async ({ cancellation_id, action, host_message, host_id }) => {
    try {
      const response = await apiClient.post(`/bookings/cancellations/respond`, {
        cancellation_id, action, host_message, host_id
      });
      return response.data;
    } catch (error) {
      logError('Error responding to cancellation:', error);
      throw error;
    }
  },
};

// Reviews API
export const reviewsAPI = {
  getByPropertyId: async (propertyId, params = {}) => {
    try {
      const response = await apiClient.get(`/reviews/property/${propertyId}/`, { params });
      return response.data;
    } catch (error) {
      logError(`Error fetching reviews for property ${propertyId}:`, error);
      throw error;
    }
  },

  create: async (propertyId, reviewData) => {
    try {
      const response = await apiClient.post(`/reviews/property/${propertyId}/`, reviewData);
      return response.data;
    } catch (error) {
      logError('Error creating review:', error);
      throw error;
    }
  },
};

// Categories API
export const categoriesAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/categories/');
      return response.data;
    } catch (error) {
      logError('Error fetching categories:', error);
      throw error;
    }
  },
};

// Host API
export const hostAPI = {
  register: async (hostData) => {
    try {
      const response = await apiClient.post('/host/register', hostData);
      return response.data;
    } catch (error) {
      logError('Error registering host:', error);
      throw error;
    }
  },

  login: async (loginData) => {
    try {
      const response = await apiClient.post('/host/login', loginData);
      return response.data;
    } catch (error) {
      logError('Error sending login OTP:', error);
      throw error;
    }
  },

  loginVerify: async (verifyData) => {
    try {
      const response = await apiClient.post('/host/login/verify', verifyData);
      return response.data;
    } catch (error) {
      logError('Error verifying login OTP:', error);
      throw error;
    }
  },

  loginPassword: async (loginData) => {
    try {
      const response = await apiClient.post('/host/login-password', loginData);
      return response.data;
    } catch (error) {
      logError('Error logging in with password:', error);
      throw error;
    }
  },

  setPassword: async (data) => {
    try {
      const response = await apiClient.post('/host/set-password', data);
      return response.data;
    } catch (error) {
      logError('Error setting host password:', error);
      throw error;
    }
  },

  forgotPassword: async (data) => {
    try {
      const response = await apiClient.post('/host/forgot-password', data);
      return response.data;
    } catch (error) {
      logError('Error sending host reset link:', error);
      throw error;
    }
  },

  resetPassword: async (data) => {
    try {
      const response = await apiClient.post('/host/reset-password', data);
      return response.data;
    } catch (error) {
      logError('Error resetting host password:', error);
      throw error;
    }
  },

  verifyOTP: async (verificationData) => {
    try {
      const response = await apiClient.post('/host/verify-otp', verificationData);
      return response.data;
    } catch (error) {
      logError('Error verifying OTP:', error);
      throw error;
    }
  },

  resendOTP: async (resendData) => {
    try {
      const response = await apiClient.post('/host/resend-otp', resendData);
      return response.data;
    } catch (error) {
      logError('Error resending OTP:', error);
      throw error;
    }
  },

  getProperties: async (hostId) => {
    try {
      const response = await apiClient.get(`/host/properties?host_id=${hostId}`);
      return response.data;
    } catch (error) {
      logError('Error fetching host properties:', error);
      throw error;
    }
  },

  getDashboard: async (hostId) => {
    try {
      const response = await apiClient.get(`/host/dashboard/${hostId}`);
      return response.data;
    } catch (error) {
      logError('Error fetching dashboard:', error);
      throw error;
    }
  },

  getAvailability: async (propertyId, month, year) => {
    try {
      let url = `/host/availability/${propertyId}`;
      if (month && year) {
        url += `?month=${month}&year=${year}`;
      }
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      logError('Error fetching availability:', error);
      throw error;
    }
  },

  blockDates: async (hostId, propertyId, dates, reason = 'blocked') => {
    try {
      const response = await apiClient.post(`/host/availability/block?host_id=${hostId}`, {
        property_id: propertyId,
        dates: dates,
        reason: reason
      });
      return response.data;
    } catch (error) {
      logError('Error blocking dates:', error);
      throw error;
    }
  },

  unblockDates: async (hostId, propertyId, dates) => {
    try {
      const response = await apiClient.post(`/host/availability/unblock?host_id=${hostId}`, {
        property_id: propertyId,
        dates: dates
      });
      return response.data;
    } catch (error) {
      logError('Error unblocking dates:', error);
      throw error;
    }
  },
};

// Admin API
export const adminAPI = {
  login: async (username, password) => {
    try {
      const response = await apiClient.post('/admin/login', { username, password });
      return response.data;
    } catch (error) {
      logError('Error logging in:', error);
      throw error;
    }
  },

  verify: async (token) => {
    try {
      const response = await apiClient.post('/admin/verify', { token });
      return response.data;
    } catch (error) {
      logError('Error verifying token:', error);
      throw error;
    }
  },

  logout: async () => {
    try {
      const response = await apiClient.post('/admin/logout');
      return response.data;
    } catch (error) {
      logError('Error logging out:', error);
      throw error;
    }
  },

  getBookings: async (filters = {}) => {
    try {
      const params = {};
      if (filters.property_id) params.property_id = filters.property_id;
      if (filters.status) params.status = filters.status;
      if (filters.host_id) params.host_id = filters.host_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.date_field) params.date_field = filters.date_field;
      const response = await apiClient.get('/admin/bookings', { params });
      return response.data;
    } catch (error) {
      logError('Error fetching admin bookings:', error);
      throw error;
    }
  },
};

export default apiClient;
